<template>
    <legal-page-layout
        :title="$t('terms.title')"
        :updated="$t('terms.updated')"
        :paragraphs="$t('terms.paragraphs')"
    ></legal-page-layout>
</template>

<script>
import LegalPageLayout from './LegalPageLayout.vue';

// Términos del servicio de Nukli. Esta URL va enlazada en la OAuth consent
// screen de Google (https://nukliapp.com/politics/terms): mantenla describiendo
// el servicio real.
export default {
    name: 'Terms',
    components: { LegalPageLayout },
    data() {
        return {
            translations: {
                en: {
                    terms: {
                        title: 'Terms of Service',
                        updated: 'Last updated: July 9, 2026',
                        paragraphs: [
                            {
                                title: '1. Who we are and what you are agreeing to',
                                text: `Nukli is a desktop email client with AI-assisted features, together with the account service and website at https://nukliapp.com. It is operated by Pasqual Mas ("Nukli", "we").
By creating a Nukli account or using the app or the website, you agree to these terms and to our Privacy Policy (https://nukliapp.com/politics/privaci). If you do not agree, do not use the service.
Contact: hola@nukliapp.com.`
                            },
                            {
                                title: '2. The service',
                                text: `Nukli lets you connect your existing email accounts (Gmail, Outlook or any IMAP provider) and read, search, organize and send your email from a desktop app, with optional AI features such as summaries, drafting help and an assistant.
Your mailboxes remain yours and stay with your email provider: Nukli accesses them with the credentials or OAuth authorization you grant, which you can revoke at any time.`
                            },
                            {
                                title: '3. Your account',
                                text: `You need a Nukli account for the online features. Keep your credentials confidential: you are responsible for the activity in your account.
You must be at least 14 years old to use Nukli.
Provide accurate information and keep it up to date.`
                            },
                            {
                                title: '4. Plans, billing and cancellation',
                                text: `Nukli offers a free plan and paid subscriptions. Payments are processed by Stripe; we do not store your card details.
- Paid plans renew automatically at the end of each billing period (monthly or yearly) until you cancel.
- You can cancel at any time from your account page; the subscription stays active until the end of the period already paid, and is not renewed after that.
- If we change prices, we will notify you in advance and the new price will apply from the next renewal.
- Statutory withdrawal and refund rights of consumers under EU and Spanish law remain unaffected.`
                            },
                            {
                                title: '5. Acceptable use',
                                text: `You agree not to use Nukli to:
- send spam or unlawful, fraudulent or abusive communications;
- infringe third-party rights or applicable law;
- attempt to breach, overload or disrupt the service or its infrastructure;
- resell or provide the service to third parties without our permission.
We may suspend or terminate accounts that breach these rules.`
                            },
                            {
                                title: '6. Your content and your email',
                                text: `Your email and the content you create belong to you. You grant us only the limited license needed to store, process and transmit that content in order to provide the service to you (sync, search, sending, and the AI features you request), as described in the Privacy Policy.
Data obtained from Google APIs is handled in accordance with the Google API Services User Data Policy, including its Limited Use requirements.`
                            },
                            {
                                title: '7. AI features',
                                text: `AI-generated output (summaries, drafts, answers) can be inaccurate or incomplete. Review it before relying on it or sending anything based on it. You are responsible for the messages you send.`
                            },
                            {
                                title: '8. Intellectual property',
                                text: `The Nukli software, brand, logo and website are our property or licensed to us. These terms do not grant you any right over them beyond the personal, non-exclusive, non-transferable right to use the service.`
                            },
                            {
                                title: '9. Availability and changes',
                                text: `We work to keep the service available and improve it continuously, but it is provided "as is" and we do not guarantee uninterrupted availability.
We may modify or discontinue features; if a change materially reduces a paid plan, we will notify you in advance.`
                            },
                            {
                                title: '10. Liability',
                                text: `To the extent permitted by law, we are not liable for indirect damages (such as loss of profit or data loss caused by third-party providers), and our total liability for claims related to the service is limited to the amounts you paid us in the 12 months before the claim.
Nothing in these terms limits rights that consumer law grants you or liability that cannot be limited by law.`
                            },
                            {
                                title: '11. Termination',
                                text: `You can stop using Nukli and delete your account at any time; your data is deleted as described in the Privacy Policy.
We may suspend or terminate your access if you materially breach these terms, notifying you when the law requires it.`
                            },
                            {
                                title: '12. Law and disputes',
                                text: `These terms are governed by Spanish law. Disputes will be submitted to the courts of the user's domicile when the user is a consumer, or of Valencia (Spain) otherwise.
The European Commission's online dispute resolution platform is available at https://ec.europa.eu/consumers/odr.
Questions about these terms: hola@nukliapp.com.`
                            }
                        ]
                    }
                },
                es: {
                    terms: {
                        title: 'Términos del Servicio',
                        updated: 'Última actualización: 9 de julio de 2026',
                        paragraphs: [
                            {
                                title: '1. Quiénes somos y qué aceptas',
                                text: `Nukli es un cliente de correo de escritorio con funciones asistidas por IA, junto con el servicio de cuentas y la web https://nukliapp.com. Lo opera Pasqual Mas ("Nukli", "nosotros").
Al crear una cuenta de Nukli o usar la app o la web, aceptas estos términos y nuestra Política de Privacidad (https://nukliapp.com/politics/privaci). Si no estás de acuerdo, no uses el servicio.
Contacto: hola@nukliapp.com.`
                            },
                            {
                                title: '2. El servicio',
                                text: `Nukli te permite conectar tus cuentas de correo existentes (Gmail, Outlook o cualquier proveedor IMAP) y leer, buscar, organizar y enviar tu correo desde una app de escritorio, con funciones opcionales de IA como resúmenes, ayuda a la redacción y un asistente.
Tus buzones siguen siendo tuyos y permanecen en tu proveedor de correo: Nukli accede a ellos con las credenciales o la autorización OAuth que concedas, que puedes revocar en cualquier momento.`
                            },
                            {
                                title: '3. Tu cuenta',
                                text: `Necesitas una cuenta de Nukli para las funciones online. Mantén tus credenciales en secreto: eres responsable de la actividad de tu cuenta.
Debes tener al menos 14 años para usar Nukli.
Facilita información veraz y mantenla actualizada.`
                            },
                            {
                                title: '4. Planes, facturación y cancelación',
                                text: `Nukli ofrece un plan gratuito y suscripciones de pago. Los pagos los procesa Stripe; no almacenamos los datos de tu tarjeta.
- Los planes de pago se renuevan automáticamente al final de cada periodo (mensual o anual) hasta que canceles.
- Puedes cancelar en cualquier momento desde tu página de cuenta; la suscripción sigue activa hasta el final del periodo ya pagado y no se renueva después.
- Si cambiamos los precios, te avisaremos con antelación y el nuevo precio se aplicará en la siguiente renovación.
- Los derechos legales de desistimiento y reembolso que la normativa europea y española reconoce a los consumidores no se ven afectados.`
                            },
                            {
                                title: '5. Uso aceptable',
                                text: `Te comprometes a no usar Nukli para:
- enviar spam ni comunicaciones ilícitas, fraudulentas o abusivas;
- infringir derechos de terceros o la ley aplicable;
- intentar vulnerar, sobrecargar o interrumpir el servicio o su infraestructura;
- revender o prestar el servicio a terceros sin nuestro permiso.
Podemos suspender o cerrar cuentas que incumplan estas normas.`
                            },
                            {
                                title: '6. Tu contenido y tu correo',
                                text: `Tu correo y el contenido que crees te pertenecen. Nos concedes solo la licencia limitada necesaria para almacenar, procesar y transmitir ese contenido con el fin de prestarte el servicio (sincronización, búsqueda, envío y las funciones de IA que pidas), tal como describe la Política de Privacidad.
Los datos obtenidos de las APIs de Google se tratan conforme a la Google API Services User Data Policy, incluidos sus requisitos de Limited Use.`
                            },
                            {
                                title: '7. Funciones de IA',
                                text: `Los resultados generados por IA (resúmenes, borradores, respuestas) pueden ser inexactos o incompletos. Revísalos antes de confiar en ellos o de enviar nada basado en ellos. Eres responsable de los mensajes que envías.`
                            },
                            {
                                title: '8. Propiedad intelectual',
                                text: `El software, la marca, el logo y la web de Nukli son de nuestra propiedad o están licenciados a nuestro favor. Estos términos no te conceden ningún derecho sobre ellos más allá del derecho personal, no exclusivo e intransferible a usar el servicio.`
                            },
                            {
                                title: '9. Disponibilidad y cambios',
                                text: `Trabajamos para mantener el servicio disponible y mejorarlo continuamente, pero se presta "tal cual" y no garantizamos una disponibilidad ininterrumpida.
Podemos modificar o retirar funciones; si un cambio reduce sustancialmente un plan de pago, te avisaremos con antelación.`
                            },
                            {
                                title: '10. Responsabilidad',
                                text: `En la medida en que la ley lo permita, no respondemos de daños indirectos (como lucro cesante o pérdidas de datos causadas por proveedores terceros), y nuestra responsabilidad total por reclamaciones relacionadas con el servicio se limita a las cantidades que nos hayas pagado en los 12 meses anteriores a la reclamación.
Nada en estos términos limita los derechos que la normativa de consumo te reconoce ni responsabilidades que la ley no permita limitar.`
                            },
                            {
                                title: '11. Terminación',
                                text: `Puedes dejar de usar Nukli y eliminar tu cuenta en cualquier momento; tus datos se borran según la Política de Privacidad.
Podemos suspender o cerrar tu acceso si incumples sustancialmente estos términos, avisándote cuando la ley lo exija.`
                            },
                            {
                                title: '12. Ley aplicable y disputas',
                                text: `Estos términos se rigen por la ley española. Las disputas se someterán a los juzgados del domicilio del usuario cuando este sea consumidor, o a los de Valencia (España) en caso contrario.
La plataforma europea de resolución de litigios en línea está disponible en https://ec.europa.eu/consumers/odr.
Dudas sobre estos términos: hola@nukliapp.com.`
                            }
                        ]
                    }
                },
                ca: {
                    terms: {
                        title: 'Termes del Servei',
                        updated: 'Última actualització: 9 de juliol de 2026',
                        paragraphs: [
                            {
                                title: '1. Qui som i què acceptes',
                                text: `Nukli és un client de correu d'escriptori amb funcions assistides per IA, juntament amb el servei de comptes i la web https://nukliapp.com. L'opera Pasqual Mas ("Nukli", "nosaltres").
En crear un compte de Nukli o usar l'app o la web, acceptes aquests termes i la nostra Política de Privacitat (https://nukliapp.com/politics/privaci). Si no hi estàs d'acord, no usis el servei.
Contacte: hola@nukliapp.com.`
                            },
                            {
                                title: '2. El servei',
                                text: `Nukli et permet connectar els teus comptes de correu existents (Gmail, Outlook o qualsevol proveïdor IMAP) i llegir, cercar, organitzar i enviar el teu correu des d'una app d'escriptori, amb funcions opcionals d'IA com resums, ajuda a la redacció i un assistent.
Les teves bústies continuen sent teves i romanen al teu proveïdor de correu: Nukli hi accedeix amb les credencials o l'autorització OAuth que concedeixis, que pots revocar en qualsevol moment.`
                            },
                            {
                                title: '3. El teu compte',
                                text: `Necessites un compte de Nukli per a les funcions online. Mantén les teves credencials en secret: ets responsable de l'activitat del teu compte.
Has de tenir almenys 14 anys per usar Nukli.
Facilita informació veraç i mantén-la actualitzada.`
                            },
                            {
                                title: '4. Plans, facturació i cancel·lació',
                                text: `Nukli ofereix un pla gratuït i subscripcions de pagament. Els pagaments els processa Stripe; no emmagatzemem les dades de la teva targeta.
- Els plans de pagament es renoven automàticament al final de cada període (mensual o anual) fins que cancel·lis.
- Pots cancel·lar en qualsevol moment des de la teva pàgina de compte; la subscripció continua activa fins al final del període ja pagat i no es renova després.
- Si canviem els preus, t'avisarem amb antelació i el nou preu s'aplicarà a la següent renovació.
- Els drets legals de desistiment i reemborsament que la normativa europea i espanyola reconeix als consumidors no es veuen afectats.`
                            },
                            {
                                title: '5. Ús acceptable',
                                text: `Et compromets a no usar Nukli per a:
- enviar spam ni comunicacions il·lícites, fraudulentes o abusives;
- infringir drets de tercers o la llei aplicable;
- intentar vulnerar, sobrecarregar o interrompre el servei o la seva infraestructura;
- revendre o prestar el servei a tercers sense el nostre permís.
Podem suspendre o tancar comptes que incompleixin aquestes normes.`
                            },
                            {
                                title: '6. El teu contingut i el teu correu',
                                text: `El teu correu i el contingut que creïs et pertanyen. Ens concedeixes només la llicència limitada necessària per emmagatzemar, processar i transmetre aquest contingut amb la finalitat de prestar-te el servei (sincronització, cerca, enviament i les funcions d'IA que demanis), tal com descriu la Política de Privacitat.
Les dades obtingudes de les APIs de Google es tracten conforme a la Google API Services User Data Policy, inclosos els seus requisits de Limited Use.`
                            },
                            {
                                title: '7. Funcions d\'IA',
                                text: `Els resultats generats per IA (resums, esborranys, respostes) poden ser inexactes o incomplets. Revisa'ls abans de confiar-hi o d'enviar res basat en ells. Ets responsable dels missatges que envies.`
                            },
                            {
                                title: '8. Propietat intel·lectual',
                                text: `El software, la marca, el logo i la web de Nukli són de la nostra propietat o estan llicenciats al nostre favor. Aquests termes no et concedeixen cap dret sobre ells més enllà del dret personal, no exclusiu i intransferible a usar el servei.`
                            },
                            {
                                title: '9. Disponibilitat i canvis',
                                text: `Treballem per mantenir el servei disponible i millorar-lo contínuament, però es presta "tal qual" i no garantim una disponibilitat ininterrompuda.
Podem modificar o retirar funcions; si un canvi redueix substancialment un pla de pagament, t'avisarem amb antelació.`
                            },
                            {
                                title: '10. Responsabilitat',
                                text: `En la mesura que la llei ho permeti, no responem de danys indirectes (com lucre cessant o pèrdues de dades causades per proveïdors tercers), i la nostra responsabilitat total per reclamacions relacionades amb el servei es limita a les quantitats que ens hagis pagat en els 12 mesos anteriors a la reclamació.
Res en aquests termes no limita els drets que la normativa de consum et reconeix ni responsabilitats que la llei no permeti limitar.`
                            },
                            {
                                title: '11. Terminació',
                                text: `Pots deixar d'usar Nukli i eliminar el teu compte en qualsevol moment; les teves dades s'esborren segons la Política de Privacitat.
Podem suspendre o tancar el teu accés si incompleixes substancialment aquests termes, avisant-te quan la llei ho exigeixi.`
                            },
                            {
                                title: '12. Llei aplicable i disputes',
                                text: `Aquests termes es regeixen per la llei espanyola. Les disputes se sotmetran als jutjats del domicili de l'usuari quan aquest sigui consumidor, o als de València (Espanya) en cas contrari.
La plataforma europea de resolució de litigis en línia està disponible a https://ec.europa.eu/consumers/odr.
Dubtes sobre aquests termes: hola@nukliapp.com.`
                            }
                        ]
                    }
                }
            }
        };
    },
    methods: {
        $t(key) {
            const lang = this.$root.currentLanguage || 'ca';
            const dict = this.translations[lang] || this.translations.en;
            const translation = key.split('.').reduce((obj, i) => obj && obj[i], dict);
            return translation || key;
        }
    }
};
</script>
